import { Link } from "react-router-dom";
import { Instagram, Facebook, Youtube, Linkedin, Phone, Mail, MapPin, ArrowUpRight, Award, LogIn } from "lucide-react";
import { CONTACT, SOCIALS, NAV_LINKS, LOGO_LIGHT, TRUST_BADGES, CLIENT_PORTAL_URL } from "../data/site";
import { ObfuscatedEmail } from "./ObfuscatedEmail";

const MC2_LOGO = "/mc2.webp";

const PinterestIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.741.099.12.112.225.085.345-.09.375-.293 1.199-.334 1.363-.053.225-.172.271-.401.165-1.495-.69-2.433-2.878-2.433-4.646 0-3.776 2.748-7.252 7.92-7.252 4.158 0 7.392 2.967 7.392 6.923 0 4.135-2.607 7.462-6.233 7.462-1.214 0-2.354-.629-2.758-1.379l-.749 2.848c-.269 1.045-1.004 2.352-1.498 3.146 1.123.345 2.306.535 3.55.535 6.607 0 11.985-5.365 11.985-11.987C23.97 5.39 18.592.026 12.017.026z"/>
  </svg>
);

const XIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231L18.244 2.25zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77z" />
  </svg>
);

const GoogleIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12.02 10.18v3.72h5.16c-.22 1.34-1.61 3.92-5.16 3.92-3.11 0-5.65-2.57-5.65-5.74s2.54-5.74 5.65-5.74c1.77 0 2.95.75 3.63 1.4l2.47-2.38C16.55 3.87 14.5 3 12.02 3 6.98 3 2.9 7.03 2.9 12s4.08 9 9.12 9c5.26 0 8.75-3.69 8.75-8.9 0-.6-.07-1.06-.15-1.52H12.02z" />
  </svg>
);

const iconFor = (name) => {
  const map = {
    instagram: Instagram,
    facebook: Facebook,
    youtube: Youtube,
    pin: PinterestIcon,
    pinterest: PinterestIcon,
    linkedin: Linkedin,
    x: XIcon,
    google: GoogleIcon,
  };
  const Ico = map[name] || ArrowUpRight;
  return <Ico size={18} />;
};

export default function Footer() {
  return (
    <>
      {/* Footer proper */}
      <footer data-testid="site-footer" className="relative bg-espresso text-[hsl(30_35%_92%)] overflow-hidden grain pb-16 lg:pb-0">
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-10 md:pt-14 pb-10 relative z-10">

        <div className="grid md:grid-cols-4 gap-10 text-center md:text-left">
          <div className="md:col-span-2 flex flex-col items-center md:items-start">
            <img src={LOGO_LIGHT} alt="Pro DJ's Abilene — Wedding DJ, Wedding Planning & Wedding Decorator logo" width="137" height="84" loading="lazy" decoding="async" className="h-[84px] w-auto object-contain mb-5" />
            <p className="font-cormorant text-xl text-[hsl(30_25%_78%)] max-w-sm leading-relaxed">
              Abilene's premier wedding DJs, event decor & coordination — adding class and sparkle to the Big Country's most memorable celebrations.
            </p>

            {TRUST_BADGES.length > 0 && (
              <div className="mt-6 flex flex-wrap gap-3 justify-center md:justify-start">
                {TRUST_BADGES.map((b) => (
                  <a
                    key={b.testid}
                    href={b.href}
                    target="_blank"
                    rel="noopener"
                    data-testid={b.testid}
                    className="group inline-flex items-center gap-2.5 rounded-full border border-[hsl(var(--gold)/0.5)] bg-[hsl(24_14%_12%)] px-4 py-2 text-[11px] uppercase tracking-[0.14em] text-[hsl(var(--gold-soft))] hover:border-[hsl(var(--gold))] hover:text-[hsl(30_40%_97%)] transition-colors duration-300"
                    title={`${b.label} — ${b.provider}`}
                  >
                    <Award size={14} className="text-[hsl(var(--gold))]" />
                    <span className="leading-tight">
                      <span className="block text-[9px] opacity-70 tracking-[0.18em]">{b.provider}</span>
                      <span className="block">{b.label}</span>
                    </span>
                  </a>
                ))}
              </div>
            )}
          </div>

          <div>
            <p className="eyebrow mb-5 opacity-90">Explore</p>
            <ul className="space-y-3">
              {NAV_LINKS.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="link-underline text-sm text-[hsl(30_25%_80%)] hover:text-[hsl(var(--gold-soft))] transition-colors duration-300">
                    {l.label}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/wedding-planning-checklist" data-testid="footer-checklist" className="link-underline text-sm text-[hsl(30_25%_80%)] hover:text-[hsl(var(--gold-soft))] transition-colors duration-300">
                  Planning Checklist
                </Link>
              </li>
              <li>
                <Link to="/blog/author/kody-mac" data-testid="footer-author-kody" className="link-underline text-sm text-[hsl(30_25%_80%)] hover:text-[hsl(var(--gold-soft))] transition-colors duration-300">
                  Meet Kody Mac — DJ &amp; MC
                </Link>
              </li>
              <li>
                <Link to="/blog/author/david-mcdonald" data-testid="footer-author-david" className="link-underline text-sm text-[hsl(30_25%_80%)] hover:text-[hsl(var(--gold-soft))] transition-colors duration-300">
                  Meet David McDonald — Coordinator
                </Link>
              </li>
              <li>
                <a
                  href={CLIENT_PORTAL_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-client-portal"
                  className="link-underline inline-flex items-center gap-1.5 text-sm text-[hsl(30_25%_80%)] hover:text-[hsl(var(--gold-soft))] transition-colors duration-300"
                >
                  <LogIn size={13} className="opacity-80" /> Client Portal Login
                </a>
              </li>
            </ul>
          </div>

          <div>
            <p className="eyebrow mb-5 opacity-90">Contact</p>
            <ul className="space-y-4 text-sm text-[hsl(30_25%_82%)]">
              <li className="flex flex-col items-center md:items-start gap-1.5">
                <span className="inline-flex items-center gap-3">
                  <Phone size={16} className="text-[hsl(var(--gold-soft))] shrink-0" />
                  <span>{CONTACT.phone}</span>
                </span>
                <span className="inline-flex items-center gap-2.5 text-[11px] uppercase tracking-[0.16em] text-[hsl(30_20%_60%)]">
                  <a href={CONTACT.phoneHref} data-testid="footer-phone" className="hover:text-[hsl(var(--gold-soft))] transition-colors duration-300">Call</a>
                  <span className="opacity-50">·</span>
                  <a href={CONTACT.smsHref} data-testid="footer-text" className="hover:text-[hsl(var(--gold-soft))] transition-colors duration-300">Text</a>
                </span>
              </li>
              <li>
                <ObfuscatedEmail
                  testId="footer-email"
                  className="inline-flex items-center gap-3 hover:text-[hsl(var(--gold-soft))] transition-colors duration-300"
                  prefix={<Mail size={16} className="text-[hsl(var(--gold-soft))] shrink-0" />}
                />
              </li>
              <li className="flex flex-col items-center md:items-start gap-1.5">
                <div className="inline-flex items-center gap-3">
                  <MapPin size={16} className="text-[hsl(var(--gold-soft))] shrink-0" />
                  <address className="not-italic text-center md:text-left">
                    <span className="block">{CONTACT.address.street}</span>
                    <span className="block">{CONTACT.address.city}, {CONTACT.address.region} {CONTACT.address.postalCode}</span>
                  </address>
                </div>
                <span className="block text-[10px] uppercase tracking-[0.14em] text-[hsl(30_20%_55%)]">Mobile Service · Serving the Big Country</span>
              </li>
            </ul>
            <div className="flex gap-3 mt-6 justify-center md:justify-start">
              {SOCIALS.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  data-testid={`footer-social-${s.icon}`}
                  className="w-10 h-10 grid place-items-center rounded-full border border-[hsl(30_25%_40%)] text-[hsl(30_25%_82%)] hover:border-[hsl(var(--gold))] hover:text-[hsl(var(--gold-soft))] transition-colors duration-300"
                >
                  {iconFor(s.icon)}
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="hairline my-10 opacity-40" />
        <div className="flex flex-col items-center gap-6 md:flex-row md:items-end md:justify-between md:gap-4 text-xs uppercase tracking-[0.16em] text-[hsl(30_20%_60%)]">
          <span className="leading-none pb-1 order-2 md:order-1">© {new Date().getFullYear()} Pro DJ's & Event Services</span>
          <div data-testid="mc2-badge" className="flex flex-col md:flex-row items-center md:items-end gap-1.5 md:gap-2.5 order-1 md:order-2">
            <span className="leading-none pb-0 md:pb-1">An MC² Company</span>
            <img
              src={MC2_LOGO}
              alt="Mc² Companies"
              width="48"
              height="60"
              loading="lazy"
              decoding="async"
              className="h-[60px] w-auto object-contain opacity-95 mc2-logo-tint"
            />
          </div>
        </div>
      </div>
    </footer>
    </>
  );
}
